import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../constant/apiConstant";
import {
  clearPendingPaystackPayment,
  getPendingPaystackPayment
} from "../utils/pendingPaystackPayment";

function clearPaymentQueryParams() {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete("reference");
  url.searchParams.delete("paymentStatus");
  url.searchParams.delete("paymentType");
  url.searchParams.delete("paymentReason");
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
}

export default function usePendingPaymentRecovery({ expectedType, onSuccess }) {
  const [paymentAlert, setPaymentAlert] = useState(null);
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    let isMounted = true;

    const runVerification = async () => {
      const query = new URLSearchParams(window.location.search);
      const queryReference = query.get("reference");
      const queryStatus = query.get("paymentStatus");
      const queryType = query.get("paymentType");
      const pendingPayment = getPendingPaystackPayment();

      let paymentReference = queryReference || pendingPayment?.reference || "";
      let paymentType = queryType || pendingPayment?.type || "";

      if (!paymentReference) {
        return;
      }

      if (expectedType && paymentType && paymentType !== expectedType) {
        return;
      }

      if (queryStatus === "failed") {
        clearPendingPaystackPayment(paymentReference);

        if (isMounted) {
          setPaymentAlert({
            severity: "error",
            text: "Payment verification failed. Please try again."
          });
        }

        clearPaymentQueryParams();
        return;
      }

      try {
        const response = await axios.get(
          `${API.BASE_URL}/api/payment/verify/${paymentReference}`,
          {
            withCredentials: true
          }
        );

        const paymentTypeFromResponse = response?.data?.payment_type || paymentType;

        if (expectedType && paymentTypeFromResponse && paymentTypeFromResponse !== expectedType) {
          return;
        }

        clearPendingPaystackPayment(paymentReference);

        if (isMounted) {
          const isWalletFunding = paymentTypeFromResponse === "wallet_funding";
          const alreadyProcessed = Boolean(response?.data?.alreadyProcessed);

          setPaymentAlert({
            severity: "success",
            text: isWalletFunding
              ? alreadyProcessed
                ? "Wallet funding already confirmed."
                : "Wallet funded successfully."
              : alreadyProcessed
                ? "Ticket payment already confirmed."
                : "Ticket purchase completed successfully."
          });
        }

        if (typeof onSuccessRef.current === "function") {
          await onSuccessRef.current(response.data);
        }
      } catch (error) {
        clearPendingPaystackPayment(paymentReference);

        if (isMounted) {
          setPaymentAlert({
            severity: "error",
            text:
              error?.response?.data?.error ||
              "Unable to verify your recent payment automatically."
          });
        }
      } finally {
        clearPaymentQueryParams();
      }
    };

    runVerification();

    return () => {
      isMounted = false;
    };
  }, [expectedType]);

  return {
    paymentAlert,
    setPaymentAlert
  };
}
